import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Role, StaffMemberDto } from '@motoschool/shared';
import { formatMoney, ROLE_LABELS } from '@motoschool/shared';
import { api, ApiRequestError } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import {
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  PageLoader,
  SectionTitle,
  Select,
  Sheet,
  SuccessNotice,
} from '../components/ui';
import { formatDateTime } from '../lib/format';
import { LoginLinkCard } from '../components/LoginLinkCard';

interface StaffResponse {
  items: StaffMemberDto[];
}

const EMPTY_FORM = {
  name: '',
  role: 'TEACHER' as Role,
  hourlyRate: '',
  isSuperAdmin: false,
};

/** Чем сотрудник представляется форме входа. */
function loginOf(member: { login: string | null; email: string | null; phone: string | null }): string {
  return member.login ?? member.email ?? member.phone ?? '—';
}

/**
 * Управление сотрудниками школы. Инструкторов заводит любой администратор,
 * администраторов — только суперадминистратор.
 */
export function StaffPage(): React.JSX.Element {
  const { user, timeZone } = useAuth();
  const queryClient = useQueryClient();
  const isSuperAdmin = user?.isSuperAdmin ?? false;

  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  /*
   * Кому сейчас показываем вход. Email и телефон при создании не спрашиваем,
   * поэтому логина у нового сотрудника нет — внутрь он попадает по ссылке.
   */
  const [linkFor, setLinkFor] = useState<StaffMemberDto | null>(null);
  const [issued, setIssued] = useState<{ name: string; login: string; password: string } | null>(
    null,
  );

  const { data, isLoading } = useQuery({
    queryKey: ['staff'],
    queryFn: () => api.get<StaffResponse>('/users/staff'),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['staff'] });

  const handleError = (caught: unknown): void => {
    setError(caught instanceof ApiRequestError ? caught.message : 'Не удалось выполнить действие');
  };

  const createStaff = useMutation({
    mutationFn: () =>
      api.post<{ user: StaffMemberDto; password?: string }>('/users', {
        name: form.name,
        role: form.role,
        hourlyRateMinor:
          form.role === 'TEACHER' && form.hourlyRate.trim() !== ''
            ? Math.round(Number(form.hourlyRate.replace(',', '.')) * 100)
            : null,
        isSuperAdmin: form.role === 'ADMIN' ? form.isSuperAdmin : undefined,
      }),
    onSuccess: async (result) => {
      await invalidate();
      setIsAdding(false);
      if (result.password) {
        setIssued({
          name: result.user.name,
          login: loginOf(result.user),
          password: result.password,
        });
      }
      setForm(EMPTY_FORM);
    },
    onError: handleError,
  });

  const resetPassword = useMutation({
    mutationFn: (member: StaffMemberDto) =>
      api
        .post<{ password: string; login: string | null }>(`/users/${member.id}/reset-password`)
        .then((result) => ({ member, ...result })),
    // Логин берём из ответа: аккаунту без контактов его придумывают здесь же,
    // и в списке, загруженном раньше, его ещё нет.
    onSuccess: async ({ member, login, password }) => {
      setIssued({ name: member.name, login: login ?? loginOf(member), password });
      setError(null);
      await invalidate();
    },
    onError: handleError,
  });

  const patchStaff = useMutation({
    mutationFn: (input: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/users/${input.id}`, input.body),
    onSuccess: async () => {
      setError(null);
      await invalidate();
    },
    onError: handleError,
  });

  if (isLoading) return <PageLoader />;

  const staff = data?.items ?? [];
  const teachers = staff.filter((item) => item.role === 'TEACHER');
  const admins = staff.filter((item) => item.role === 'ADMIN');

  const renderMember = (member: StaffMemberDto): React.JSX.Element => {
    const isSelf = member.id === user?.id;

    return (
      <Card key={member.id} className={member.isActive ? '' : 'opacity-60'}>
        <div className="flex items-start gap-3">
          <Avatar name={member.name} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="truncate font-medium">{member.name}</p>
              {member.isSuperAdmin && <Badge tone="brand">Суперадмин</Badge>}
              {!member.isActive && <Badge tone="neutral">отключён</Badge>}
              {isSelf && <Badge tone="neutral">это вы</Badge>}
            </div>
            <p className="truncate text-xs text-muted">
              Логин: <span className="tabular-nums">{loginOf(member)}</span>
            </p>
            <p className="mt-1 text-xs text-muted">
              {member.role === 'TEACHER'
                ? `${member.studentCount} учеников${
                    member.hourlyRateMinor === null
                      ? ''
                      : ` · ${formatMoney(member.hourlyRateMinor)} за час`
                  }`
                : ROLE_LABELS[member.role]}
              {member.lastLoginAt
                ? ` · заходил ${formatDateTime(member.lastLoginAt, timeZone)}`
                : ' · ещё не заходил'}
            </p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {/* Рабочие часы есть только у инструкторов: внутри них создаются окна */}
          {member.role === 'TEACHER' && (
            <Link to={`/working-hours?teacher=${member.id}`}>
              <Button variant="secondary">Часы работы</Button>
            </Link>
          )}

          <Button variant="secondary" onClick={() => setLinkFor(member)}>
            Ссылка на вход
          </Button>

          <Button
            variant="secondary"
            isLoading={resetPassword.isPending}
            onClick={() => resetPassword.mutate(member)}
          >
            Сбросить пароль
          </Button>

          {/* Себя отключать нельзя: иначе можно закрыть себе доступ одним нажатием */}
          {!isSelf && (
            <Button
              variant={member.isActive ? 'danger' : 'secondary'}
              isLoading={patchStaff.isPending}
              onClick={() =>
                patchStaff.mutate({ id: member.id, body: { isActive: !member.isActive } })
              }
            >
              {member.isActive ? 'Отключить' : 'Включить'}
            </Button>
          )}

          {isSuperAdmin && member.role === 'ADMIN' && (
            <Button
              variant="secondary"
              isLoading={patchStaff.isPending}
              onClick={() =>
                patchStaff.mutate({
                  id: member.id,
                  body: { isSuperAdmin: !member.isSuperAdmin },
                })
              }
            >
              {member.isSuperAdmin ? 'Снять суперадмина' : 'Сделать суперадмином'}
            </Button>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Сотрудники</h1>
          <p className="text-sm text-muted">Инструкторы и администраторы школы</p>
        </div>
        <Button
          onClick={() => {
            setError(null);
            setIssued(null);
            setIsAdding(true);
          }}
        >
          <span aria-hidden>＋</span> Добавить
        </Button>
      </div>

      {error && <ErrorNotice message={error} />}

      {issued && (
        <SuccessNotice>
          <p className="font-medium">Доступ для {issued.name}</p>
          <p className="mt-1">
            Логин: <b>{issued.login}</b> · Пароль: <b>{issued.password}</b>
          </p>
          <p className="mt-1 text-xs">
            Пароль показывается один раз — передайте его сотруднику.{' '}
            <button type="button" className="underline" onClick={() => setIssued(null)}>
              Скрыть
            </button>
          </p>
        </SuccessNotice>
      )}

      <section>
        <SectionTitle>Инструкторы</SectionTitle>
        {teachers.length === 0 ? (
          <EmptyState
            title="Инструкторов пока нет"
            description="Пока не добавлен ни один инструктор, расписание на сайте будет пустым."
          />
        ) : (
          <div className="space-y-2">{teachers.map(renderMember)}</div>
        )}
      </section>

      <section>
        <SectionTitle>Администраторы</SectionTitle>
        <div className="space-y-2">{admins.map(renderMember)}</div>
        {!isSuperAdmin && (
          <p className="mt-2 text-xs text-muted">
            Добавлять и разжаловать администраторов может только суперадминистратор.
          </p>
        )}
      </section>

      <Sheet
        isOpen={linkFor !== null}
        title={linkFor ? `Вход — ${linkFor.name}` : 'Вход'}
        onClose={() => setLinkFor(null)}
        footer={
          <div className="pb-1">
            <Button variant="secondary" fullWidth onClick={() => setLinkFor(null)}>
              Закрыть
            </Button>
          </div>
        }
      >
        {linkFor && (
          <LoginLinkCard
            userId={linkFor.id}
            subject={linkFor.role === 'TEACHER' ? 'Инструктор' : 'Сотрудник'}
          />
        )}
      </Sheet>

      <Sheet
        isOpen={isAdding}
        title="Новый сотрудник"
        onClose={() => setIsAdding(false)}
        footer={
          <div className="flex gap-2 pb-1">
            <Button variant="secondary" fullWidth onClick={() => setIsAdding(false)}>
              Отмена
            </Button>
            <Button fullWidth isLoading={createStaff.isPending} onClick={() => createStaff.mutate()}>
              Добавить
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {error && <ErrorNotice message={error} />}

          <p className="rounded-xl bg-canvas px-3 py-2 text-sm text-muted">
            Email и телефон не нужны: логин и пароль придумаются сами и покажутся
            после сохранения. Если так удобнее — сотруднику можно вместо них
            отправить ссылку на вход.
          </p>

          <Field label="Роль">
            <Select
              value={form.role}
              onChange={(event) =>
                setForm({ ...form, role: event.target.value as Role, isSuperAdmin: false })
              }
            >
              <option value="TEACHER">Инструктор</option>
              {/* Пункт доступен только суперадмину — API проверяет это повторно */}
              {isSuperAdmin && <option value="ADMIN">Администратор</option>}
            </Select>
          </Field>

          <Field label="Имя и фамилия" required>
            <Input
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </Field>

          {form.role === 'TEACHER' && (
            <Field label="Ставка за час" hint="Из неё считается цена занятия в расписании">
              <Input
                value={form.hourlyRate}
                onChange={(event) => setForm({ ...form, hourlyRate: event.target.value })}
                inputMode="decimal"
                placeholder="8000"
              />
            </Field>
          )}

          {form.role === 'ADMIN' && isSuperAdmin && (
            <label className="flex items-start gap-3 rounded-xl border border-line p-3">
              <input
                type="checkbox"
                checked={form.isSuperAdmin}
                onChange={(event) => setForm({ ...form, isSuperAdmin: event.target.checked })}
                className="mt-0.5 size-5 accent-brand-600"
              />
              <span className="text-sm">
                <b>Суперадминистратор</b>
                <br />
                <span className="text-muted">
                  Сможет заводить и разжаловать других администраторов
                </span>
              </span>
            </label>
          )}

        </div>
      </Sheet>
    </div>
  );
}
